import React, { FC } from 'react';
import styled from '@emotion/styled';
import {
  ProductBundleProperty,
  ProductPageQuery,
} from '../../@types/codegen/graphql';
import {
  spacingBottom,
  spacingTop,
  mediaQueries,
  spacingLeft,
  spacingRight,
  spacingTopBottom,
  FontSecondaryRegular,
} from '@digando/react-component-library';
import { generateProductTitle } from './helper/generate-product-title';
import { useTranslation } from 'next-i18next';
import { getFormattedPropertyValue } from '../../helper/readable-product-property';
import { FactList, IFactListFact } from '../elements/FactList/FactList';
import { Title } from '../elements/Typography/Title/Title';

// import marked from 'marked';

export interface ITopInfoProps {
  productBundle: ProductPageQuery['productBundle'];
}

export const TopInfo: FC<ITopInfoProps> = ({ productBundle }) => {
  const [t] = useTranslation(['product-page']);
  const bundle = productBundle?.bundle;
  const type = bundle?.type;

  const facts: IFactListFact[]
    = bundle?.properties
      .filter((property) => true === property.isFact)
      .map((property, index) => {
        return {
          id: index,
          name: property.descriptor.title ?? property.key,
          // @todo: Check typing issue
          value: getFormattedPropertyValue((property as unknown) as ProductBundleProperty),
        };
      }) ?? [];

  const categoryTitle = bundle?.type.mainCategory?.title ?? '';

  return (
    <>
      <TitleWrapper>
        <CategoryTitle>
          {categoryTitle}
        </CategoryTitle>
        <Title level={1} variant={2}>
          {bundle && generateProductTitle(bundle.type, bundle.properties)}
        </Title>
      </TitleWrapper>

      <TextWrapper>
        {type?.text && (<p>{type.text}</p>)}
      </TextWrapper>

      {0 < facts.length && (
        <>
          <TitleWrapper>
            <Title level={4} variant={3} type="secondary">
              {t('product-page:top-info.key-data-title')}
            </Title>
          </TitleWrapper>
          <MobileWrapper>
            <GridWrapper className="grid-mobile">
              <FactList facts={facts}/>
            </GridWrapper>
          </MobileWrapper>
          <DesktopWrapper>
            <GridWrapper>
              <FactList fill={false} facts={facts.filter((i, index) => 0 === index % 2)}/>
              <FactList fill={false} facts={facts.filter((i, index) => 1 === index % 2)}/>
            </GridWrapper>
          </DesktopWrapper>
        </>
      )}
    </>
  );
};

const DesktopWrapper = styled.div`
  display: none;

  .ant-list-item > div {
    line-height: 20px;
  }

  @media ${mediaQueries.tablet} {
    display: inherit;
  }
`;

const MobileWrapper = styled.div`
  ${spacingBottom(1)};
  grid-template-columns: 1fr;

  span:last-child {
    ${spacingTop(2)};
    justify-self: center;
    cursor: pointer;
  }

  @media ${mediaQueries.tablet} {
    display: none;
  }

  .grid-mobile {
    margin-bottom: 10px !important;
  }
`;

const TextWrapper = styled.div`
  ${spacingBottom(4)};
`;

const TitleWrapper = styled.div`
  ${spacingBottom(2)};
  color: var(--secondary-color);
`;

const CategoryTitle = styled.span`
    ${spacingBottom(2)};
    color: var(--secondary-color);
    ${FontSecondaryRegular};
    font-size: var(--font-size-headline-h4);
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: 10px 0;
  border-top: 1px solid var(--black);
  border-bottom: 1px solid var(--black);


  .ant-list-item {
    margin: 0;
    ${spacingLeft(0)};
    ${spacingRight(0)};
    ${spacingTopBottom(1)};

    > div {
      font-size: var(--font-size-text-small);
      line-height: 22px;
    }

    div:first-of-type {
      text-align: left;
    }

    div:last-of-type {
      text-align: right;
    }
  }

  @media ${mediaQueries.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-gap: 3vw;
  }

  @media ${mediaQueries.laptopL} {
    grid-gap: 70px;
  }
`;
